import type { StoreProvider } from "@hobbyshopify/backend-client";

/**
 * The four provider states the UX brief names, rendered identically wherever a
 * provider is shown (Settings, the onboarding wizard, the dashboard checklist).
 *
 * PENDING splits in two, because "we need something from you" and "we are
 * checking what you sent" are different news and only one of them is the
 * merchant's move.
 */
export function providerStatusLabel(connection: StoreProvider | null | undefined): string {
  if (!connection || connection.status === "DISCONNECTED") return "Not connected";
  if (connection.status === "PENDING") {
    return isWaitingOnProvider(connection) ? "Under review" : "Onboarding incomplete";
  }
  if (connection.status === "ERROR") return "Connection problem";
  return "Connected";
}

/**
 * Returns a `badge-*` modifier, so the pill picks up the shared shape from
 * `index.css` and only chooses its colour here. Connected is the accent — it is
 * the one provider state that means "you can take money" — and the two broken
 * states split into amber (waiting on you) and red (failed).
 */
export function providerStatusClass(connection: StoreProvider | null | undefined): string {
  if (connection?.status === "ACTIVE") return "badge-accent";
  if (connection?.status === "ERROR") return "badge-danger";
  if (connection?.status === "PENDING") return "badge-warn";
  return "badge-neutral";
}

/**
 * Whether the provider is working on what it already has, with nothing left to
 * ask the merchant. Shopify publishes no `merchantAction`, so it is never this.
 */
export function isWaitingOnProvider(connection: StoreProvider | null | undefined): boolean {
  return connection?.merchantAction === "WAIT";
}

/**
 * Whether sending the merchant to the provider's hosted onboarding will show
 * them anything.
 *
 * False only for the two states where it will not: the provider is verifying, so
 * there are no fields to collect and it hands the merchant straight back to us —
 * a click that looks like it did nothing — or it has refused the account
 * outright. Offering the button anyway is the loop this answers.
 */
export function canStartProviderOnboarding(connection: StoreProvider | null | undefined): boolean {
  return connection?.merchantAction !== "WAIT" && connection?.merchantAction !== "SUPPORT";
}

/**
 * The merchant-facing sentence for a connection that is not yet live.
 *
 * Derived here rather than echoing `lastError`: that field is written for a log
 * — `Stripe requires action: requirements.past_due.` — and putting it in front
 * of a merchant leaks an API field name where a next step belongs. The raw
 * string stays available through `providerErrorDetail` for support.
 */
export function providerMessage(connection: StoreProvider | null | undefined, providerName: string): string | null {
  if (!connection || connection.status === "DISCONNECTED" || connection.status === "ACTIVE") return null;

  const payoff = connection.kind === "PAYMENT" ? "before you can take payments" : "before the connection goes live";

  if (connection.status === "ERROR") {
    return connection.merchantAction === "SUPPORT"
      ? `${providerName} won't enable this account. Only ${providerName} can say why — their dashboard has the details.`
      : `We couldn't get this account's status from ${providerName}. Check again in a moment; if it keeps failing, disconnect and connect it fresh.`;
  }

  if (isWaitingOnProvider(connection)) {
    return `${providerName} is reviewing what you submitted. Nothing to fill in — this updates on its own once they're done.`;
  }

  return connection.externalAccountRef
    ? `${providerName} still needs a few details from you ${payoff}. Picking up where you left off takes a couple of minutes.`
    : `Setup hasn't finished. ${providerName} will ask for your business details and a bank account.`;
}

/** The provider's own wording, for the collapsed detail line. */
export function providerErrorDetail(connection: StoreProvider | null | undefined): string | null {
  if (!connection || connection.status === "ACTIVE" || connection.status === "DISCONNECTED") return null;
  return connection.lastError;
}
